import { axiosInstance } from '@/shared/api/axios';
import { TableResultType } from '@/shared/types/api.type';
import {
  SpnServiceCodeGetRequestType,
  SpnServiceCodeGetResponseType,
  SpnRiskSupportPostRequestType,
  SpnRiskSupportPostResponseType,
  SpnSearchResultRequest,
  SpnSearchResultModel,
  SpnReportDownloadRequest,
  SpnReportDownloadResponse,
} from '@/shared/types/api/api.spn';

export const getSpnServiceCodes = async ({
  type,
}: SpnServiceCodeGetRequestType): Promise<SpnServiceCodeGetResponseType> => {
  const { data } = await axiosInstance.get<SpnServiceCodeGetResponseType>(`/spn/service-codes`, {
    params: {
      type,
    },
  });
  return data;
};

export const submitSpnRiskSupport = async ({
  type,
  spnServiceCode,
  content,
}: SpnRiskSupportPostRequestType): Promise<SpnRiskSupportPostResponseType> => {
  const { data } = await axiosInstance.post<SpnRiskSupportPostResponseType>(`/spn/risk-support`, {
    type,
    spnServiceCode,
    content,
  });
  return data;
};

export const getSpnSearchResults = async ({
  pageIndex,
  pageSize,
  groupId,
  spnExecutionHistoryId,
  condition,
}: SpnSearchResultRequest): Promise<TableResultType<SpnSearchResultModel[]>> => {
  const { data } = await axiosInstance.get<TableResultType<SpnSearchResultModel[]>>(
    `/group/${groupId}/spn-execution/${spnExecutionHistoryId}/list`,
    {
      params: {
        offset: (pageIndex - 1) * pageSize,
        limit: pageSize,
        birthyearFrom: condition?.birthyear?.start,
        birthyearTo: condition?.birthyear?.end,
        ageFrom: condition?.age?.start,
        ageTo: condition?.age?.end,
        pubDateFrom: condition?.pubDate?.start,
        pubDateTo: condition?.pubDate?.end,
      },
    },
  );
  return data;
};

const extractFileName = (contentDisposition?: string): string | undefined => {
  if (!contentDisposition) return undefined;

  const encodedFileNameMatch = contentDisposition.match(/filename\*\s*=\s*([^;]+)/i);
  if (encodedFileNameMatch) {
    const encodedValue = encodedFileNameMatch[1].trim().replace(/^"|"$/g, '');
    const utf8Prefix = "UTF-8''";
    const fileName = encodedValue.startsWith(utf8Prefix)
      ? encodedValue.slice(utf8Prefix.length)
      : encodedValue;
    try {
      return decodeURIComponent(fileName);
    } catch {
      return fileName;
    }
  }

  const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return fileNameMatch?.[1];
};

export const downloadSpnReport = async ({
  groupId,
  spnExecutionHistoryId,
}: SpnReportDownloadRequest): Promise<SpnReportDownloadResponse> => {
  const response = await axiosInstance.post<ArrayBuffer>(
    `/group/${groupId}/spn-execution/${spnExecutionHistoryId}/file`,
    undefined,
    {
      responseType: 'arraybuffer',
    },
  );

  const contentDisposition = response.headers['content-disposition'];
  const fileName = extractFileName(contentDisposition);
  const contentType = response.headers['content-type'];

  return {
    fileName,
    contentType,
    fileContent: response.data,
  };
};
